export abstract class AbstractStorage {

  /**
   * this needs to return Storage object if available
   * @protected
   */
  protected abstract getStorage(): Storage | undefined;

  /**
   * get item from storage if available
   * @param key
   */
  public getItem<ItemType extends string = string>(key: string): ItemType | undefined {
    try {
      const result = this.getStorage()?.getItem(key);
      return result ? result as ItemType : undefined;
    } catch (_e) {
      // we don't have access, return undefined
      return undefined;
    }
  }

  /**
   * get item from storage and parse item as JSON if available
   * @param key
   */
  getJsonItem<ItemType = unknown>(key: string): ItemType | undefined {
    const itemAsString = this.getItem(key);
    let itemFromJson;
    if (itemAsString !== undefined && itemAsString !== null) {
      itemFromJson = JSON.parse(itemAsString) as ItemType;
    }
    return itemFromJson;
  }

  getOrCreateNumberValue(key: string, defaultValue: number): number {
    let result = this.getJsonItem<number>(key);

    if (result === undefined) {
      result = defaultValue;
      this.setJsonItem<number>(key, result);
    }

    return result;
  }

  getOrCreateBooleanValue(key: string, defaultValue: boolean): boolean {
    let result = this.getJsonItem<boolean>(key);

    if (result === undefined) {
      result = defaultValue;
      this.setJsonItem<boolean>(key, result);
    }

    return result;
  }

  getOrCreateStringValue(key: string, defaultValue: string): string {
    let result = this.getItem<string>(key);

    if (result === undefined) {
      result = defaultValue;
      this.setJsonItem<string>(key, result);
    }

    return result;
  }

  /**
   * set item to storage if storage is available
   * @param key
   * @param item
   */
  public setItem<ItemType extends string>(key: string, item: ItemType): void {
    try {
      this.getStorage()?.setItem(key, item);
    } catch (_e) {
      // we have no access to storage, nothing we can do
    }
  }

  /**
   * convert item to JSON string and store item to storage if storage is available
   * @param key
   * @param item
   */
  public setJsonItem<ItemType>(key: string, item: ItemType): void {
    this.setItem(key, JSON.stringify(item));
  }


  /**
   * remove item from storage if storage is available
   * @param key
   */
  public removeItem(key: string): void {
    try {
      this.getStorage()?.removeItem(key);
    } catch (_e) {
      // we have no access to storage, nothing we can do
    }
  }


  /**
   * clear all items in storage if storage is available
   */
  public clear(): void {
    try {
      this.getStorage()?.clear();
    } catch (_e) {
      // we have no access to storage, nothing we can do
    }
  }
}
